import { ReactNode } from "react";

type Props = {
  title: string;
  /** Optional one-liner explaining what belongs in this group. */
  hint?: string;
  children: ReactNode;
};

/**
 * A labelled group of form fields.
 *
 * The create/edit forms were a flat run of ~15 identical two-column rows, which gave
 * the reader no way to find anything — every field looked equally important. Grouping
 * them under headings turns one long wall into five short, scannable blocks.
 */
export default function FormSection({ title, hint, children }: Props) {
  return (
    <section className="rounded-2xl border border-edge bg-surface p-5">
      <div className="mb-4">
        <p className="flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
          <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
          {title}
        </p>
        {hint ? (
          <p className="mt-1.5 text-[12px] font-medium text-light-gray">{hint}</p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-x-5 gap-y-4 smallTablet:grid-cols-1">
        {children}
      </div>
    </section>
  );
}

/** Makes a child span the full width of the section grid. */
export function FormRow({ children }: { children: ReactNode }) {
  return <div className="col-span-2 smallTablet:col-span-1">{children}</div>;
}
